import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import TableComponent from "../TableComponent";
import Pagination from "../../view/common/pagination/Pagination";
import { updateFilters, resetFilters } from "../../redux/slices/filterSlice";
import { RootState } from "../../redux/slices/store";
import { getData } from "../../api/ApiServices";
import "../../styles/StockExpiry.css"; 

const StockExpiry: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const filters = useSelector((state: RootState) => state.filters);

  const [expiredStocksData, setExpiredStocksData] = useState<any[]>([]);
  const [originalData, setOriginalData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasNoRecords, setHasNoRecords] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const [sortBy, setSortBy] = useState<string>("expiredDays");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [isFiltering, setIsFiltering] = useState<boolean>(false);
  const [lastFilterState, setLastFilterState] = useState<any>({});
  const excludeFieldsForRacks = ["createdBy"];

  const columns = [
    {
      key: "parts",
      label: "Part No",
      nameKey: "internalPartNo",
      render: (value: any) =>
        typeof value === "object" ? value?.internalPartNo : "N/A",
      sortable: true,
    },
    {
      key: "parts",
      label: "Part Name",
      nameKey: "partName",
      render: (value: any) =>
        typeof value === "object" ? value?.partName : "N/A",
      sortable: true,
    },
    {
      key: "category",
      label: "Category",
      nameKey: "name",
      render: (value: any) =>
        typeof value === "object" ? value?.name : "N/A",
      sortable: true,
    },
    {
      key: "grns",
      label: "GRN Date",
      nameKey: "grnDate",
      render: (value: any) =>
        typeof value === "object" && value?.grnDate
          ? new Date(value.grnDate).toLocaleDateString()
          : "N/A",
      sortable: true,
    },
    {
      key: "parts",
      label: "Self Life (Days)",
      nameKey: "expiryDays",
      render: (value: any) =>
        typeof value === "object" ? value?.expiryDays : "N/A",
      sortable: true,
      isRightAlign: true,
    },
    { 
      key: "qty", 
      label: "Quantity", 
      sortable: true,
      isRightAlign: true,
    },
    { 
      key: "expiredDays", 
      label: "Expired Days", 
      sortable: true,
      isRightAlign: true,
      render: (value: number) => (
        <span className={value > 0 ? "expired-status" : "warning-status"}>
          {value}
        </span>
      )
    },
  ];

  const handleSortChange = (
    sortByField: string,
    sortOrderDirection: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderDirection);
  };

  useEffect(() => {
    const hasFilterChanged = JSON.stringify(filters) !== JSON.stringify(lastFilterState);
    
    if (hasFilterChanged) {
      setIsFiltering(Object.keys(filters).length > 0);
      setLastFilterState(filters);
      
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchExpiredStocks();
      }
    }
  }, [filters]);

  useEffect(() => {
    fetchExpiredStocks();
  }, [currentPage, itemsPerPage, sortBy, sortOrder]);

  const fetchExpiredStocks = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response: any = await getData("/stocks/expired", {
        page: currentPage,
        limit: itemsPerPage,
        filter: filters,
        sortBy: sortBy,
        sortOrder: sortOrder,
      });
      
      console.log("API Response for Expired Stocks:", response);
      
      if (response?.data?.data) {
        setExpiredStocksData(response.data.data);
        
        if (currentPage === 1 && !isFiltering && response.data.data.length > 0) {
          setOriginalData(response.data.data);
        }
        
        setTotalItems(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.totalPages || 0);
        setHasNoRecords(response.data.data.length === 0);
      } else {
        setExpiredStocksData([]);
        setHasNoRecords(true);
      }
    } catch (err) {
      console.error("Error fetching expired stock data:", err);
      setError("Failed to load expired stocks data. Please try again later.");
      setExpiredStocksData([]);
      setTotalItems(0);
      setTotalPages(0);
      setHasNoRecords(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = useCallback((filters: Record<string, any>) => {
    Object.entries(filters).forEach(([key, value]) => {
      dispatch(updateFilters({ key, value: String(value) }));
    });
  }, [dispatch]);

  return (
    <div className="item-models-container">
      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>Stocks</span>
              <span>Expired Stocks</span>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="stockexpiry-error-message">{error}</div>}

      <div className="box-container">
        <div className="table-wrapper">
          <TableComponent
            columns={columns}
            data={expiredStocksData}
            setFilter={handleFilterChange}
            setSorting={handleSortChange}
            defaultSortConfig={{ key: sortBy, direction: sortOrder }}
            excludeFields={excludeFieldsForRacks} 
            context="stockexpire" 
          />
          
          {isLoading && (
            <div className="loading-overlay">
              <div className="spinner"></div>
            </div>
          )}
        </div>
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          totalItems={totalItems}
        />
      </div>
    </div>
  );
};

export default StockExpiry;